import React, { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import InspectorOverview from "./InspectorOverview";
import { INSPECTOR_PANIC_STEPS } from "./panicConfig";
import { usePanicSession } from "./usePanicSession";

export default function InspectorPortalPage() {
  const [me, setMe] = useState(null);
  const { history, historyLoading, loadHistory } = usePanicSession(INSPECTOR_PANIC_STEPS);

  useEffect(() => {
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((data) => setMe(data || null))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    const currentUrl = window.location.href;
    window.history.replaceState({ inspectorPortal: true }, "", currentUrl);
    window.history.pushState({ inspectorPortal: true }, "", currentUrl);

    function blockBackNavigation() {
      window.history.pushState({ inspectorPortal: true }, "", currentUrl);
      window.location.replace("/ispettore");
    }

    window.addEventListener("popstate", blockBackNavigation);
    return () => window.removeEventListener("popstate", blockBackNavigation);
  }, []);

  useEffect(() => {
    loadHistory(null, 20, "company");
  }, [loadHistory]);

  async function handleLogout() {
    try {
      await fetch("/api/logout.php", { method: "POST", credentials: "include" });
    } finally {
      window.location.replace("/login");
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-600">Portale ispettore</div>
          <div className="mt-2 text-2xl font-bold text-slate-950">{me?.azienda_nome || "Area ispettore"}</div>
          <div className="mt-2 text-sm text-slate-600">
            Accesso dedicato alla sola documentazione ispettiva dell'azienda.
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={handleLogout} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-slate-50">
              Esci
            </button>
          </div>
        </div>

        <InspectorOverview steps={INSPECTOR_PANIC_STEPS} />
        <InspectorHistoryCard items={history} loading={historyLoading} />
      </div>
    </div>
  );
}

function InspectorHistoryCard({ items, loading }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Report salvati</div>
          <div className="mt-1 text-lg font-bold text-slate-950">Storico ispezioni dell'azienda</div>
          <div className="mt-1 text-sm text-slate-600">Apri i log finali gia salvati senza uscire dall'area ispettore.</div>
        </div>
        <div className="text-xs font-semibold text-slate-500">{items.length} report</div>
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-slate-600">Caricamento report...</div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Nessun report Panic Mode trovato per questa azienda.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((item) => {
            const exportUrl = `/api/panic/export-log.php?session_id=${encodeURIComponent(item.id)}`;
            return (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-slate-950">{item.activity_type_label || "Ispezione Panic Mode"}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Avviata {formatDateTime(item.started_at || item.created_at)}
                      {item.ended_at ? ` · Chiusa ${formatDateTime(item.ended_at)}` : ""}
                    </div>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-700">
                    {String(item.status || "draft").toUpperCase()}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full bg-white px-2 py-1 font-semibold">
                    {item.steps_done}/{item.steps_total || 0} step
                  </span>
                  <span className="rounded-full bg-white px-2 py-1 font-semibold">
                    {item.non_ok_count || 0} NON OK
                  </span>
                  {item.responsible_name && (
                    <span className="rounded-full bg-white px-2 py-1 font-semibold">
                      Resp. {item.responsible_name}
                    </span>
                  )}
                </div>

                <div className="mt-3">
                  <a
                    href={exportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-slate-100"
                  >
                    <FileText size={16} />
                    Apri log finale
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
